/**
 * React Query hooks for WorkoutTemplate CRUD.
 * Replaces Zustand mock data for the Workout Builder and Template Library.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createWorkoutTemplate,
  deleteWorkoutTemplate,
  duplicateWorkoutTemplate,
  fetchWorkoutTemplate,
  fetchWorkoutTemplates,
  updateWorkoutTemplate,
  addWorkoutSegment,
  updateWorkoutSegment,
  deleteWorkoutSegment,
  reorderWorkoutSegments,
  type WorkoutTemplateCreate,
  type WorkoutTemplateUpdate,
  type WorkoutSegmentCreate,
} from "@/lib/templateApi";
import { useAuthState } from "./useAuthState";

const QUERY_KEY = "workoutTemplates";

// ── Queries ───────────────────────────────────────────────────────────────────

export function useWorkoutTemplates(params?: {
  sport?: string;
  difficulty?: string;
  search?: string;
  limit?: number;
  offset?: number;
}) {
  const { isLoaded, isAuthenticated } = useAuthState();

  return useQuery({
    queryKey: [QUERY_KEY, params],
    queryFn: () => fetchWorkoutTemplates(params),
    enabled: isLoaded && isAuthenticated,
    staleTime: 5 * 60 * 1000,
  });
}

export function useWorkoutTemplate(id: string | null) {
  const { isLoaded, isAuthenticated } = useAuthState();

  return useQuery({
    queryKey: [QUERY_KEY, id],
    queryFn: () => fetchWorkoutTemplate(id!),
    enabled: isLoaded && isAuthenticated && !!id,
    staleTime: 10 * 60 * 1000,
  });
}

// ── Mutations ─────────────────────────────────────────────────────────────────

export function useCreateWorkoutTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: WorkoutTemplateCreate) => createWorkoutTemplate(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

export function useUpdateWorkoutTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: WorkoutTemplateUpdate }) =>
      updateWorkoutTemplate(id, payload),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
      qc.setQueryData([QUERY_KEY, data.id], data);
    },
  });
}

export function useDeleteWorkoutTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteWorkoutTemplate(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

export function useDuplicateWorkoutTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => duplicateWorkoutTemplate(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

// ── Segment mutations ─────────────────────────────────────────────────────────

export function useAddWorkoutSegment(templateId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: WorkoutSegmentCreate) => addWorkoutSegment(templateId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY, templateId] });
    },
  });
}

export function useUpdateWorkoutSegment(templateId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ segmentId, payload }: { segmentId: string; payload: WorkoutSegmentCreate }) =>
      updateWorkoutSegment(templateId, segmentId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY, templateId] });
    },
  });
}

export function useDeleteWorkoutSegment(templateId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (segmentId: string) => deleteWorkoutSegment(templateId, segmentId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY, templateId] });
    },
  });
}

export function useReorderWorkoutSegments(templateId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orderedIds: string[]) => reorderWorkoutSegments(templateId, orderedIds),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY, templateId] });
    },
  });
}
